import './index.css'

import cuid from 'cuid'
import React from 'react'
import NotificationSystem from 'react-notification-system'

import NavigationService from '@disco/state-management/NavigationService'
import { setNotificationSystemRef } from '@disco/utils/notifications'

import type { UINotification } from 'types/notification'

import { Flex, Spinner, Text } from 'components/ds'
import ContactUsLink from 'components/ui/contact-us-link'

const style = {
  Containers: {
    bl: {
      bottom: 0,
    },
  },
  NotificationItem: {
    DefaultStyle: {
      background: '#fff',
      borderLeft: '4px solid',
      borderRadius: 3,
      borderTop: 'none',
      boxShadow:
        '0 8px 12px rgba(9, 30, 66, 0.15), 0 0 1px rgba(9, 30, 66, 0.30)',
      color: '#172b4d',
      padding: 20,
    },
    success: {
      // Applied only to the success notification item
      borderColor: '#34b57c',
    },
    error: {
      borderColor: 'tomato',
    },
    warning: {
      borderColor: '#FFAB00',
    },
  },
  Title: {
    DefaultStyle: {
      color: 'inherit',
      fontWeight: 500,
    },
  },
  MessageWrapper: {
    DefaultStyle: {
      whiteSpace: 'pre-wrap',
      margin: 0,
      padding: 0,
    },
  },
  Action: {
    DefaultStyle: {
      backgroundColor: 'transparent',
      borderRadius: 0,
      color: '#0052cc',
      fontWeight: 500,
      margin: '5px 0 0',
      padding: 0,
    },
  },
  Dismiss: {
    DefaultStyle: {
      color: '#ffffff',
      backgroundColor: 'tomato',
    },
  },
}
const notificationDismissDelays = {
  success: 2,
  error: 10,
  warning: 5,
}

type Props = Record<string, never>

type State = {
  listNotifications: Record<
    string,
    UINotification & {
      uid: number | string
    }
  >
}
/**
 * Parse notification message string to handle special content markers and interactive elements
 * @param message - Message string that may contain JSON markers for special content
 * @param retry - Retry function to call when retry elements are clicked
 */
export const parseMessage = ({
  message,
  retry,
}: {
  message?: string
  retry: (...args: Array<any>) => any
}): null | Array<
  any | React.ReactElement<'span'> | React.ReactNode | string
> => {
  if (!message) return null
  const content = []
  message.split('|').forEach(text => {
    if (text.startsWith('{')) {
      const data: {
        retryMessage?: string
        contactUsLink?: string
        intercomMsg?: string
        externalLink?: Record<string, any>
      } = JSON.parse(text)

      if (data.retryMessage) {
        content.push(
          <span className="UINotificationSystem_RetryLabel" onClick={retry}>
            {data.retryMessage}
          </span>,
        )
      }

      if (data.externalLink) {
        content.push(
          <a
            href={data.externalLink.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {data.externalLink.label}
          </a>,
        )
      }

      if (data.contactUsLink) {
        content.push(
          <ContactUsLink intercomMsg={data.intercomMsg}>
            {data.contactUsLink}
          </ContactUsLink>,
        )
      }
    } else {
      content.push(text)
    }
  })
  return content
}

/**
 * UI notification system component that manages toast notifications across the application
 * Handles notification display, styling, auto-dismissal, and user interactions
 * Supports success, error, warning notifications with custom actions and retry functionality
 */
class UINotificationSystem extends React.Component<Props, State> {
  state = {
    listNotifications: {},
  }
  _notifications:
    | {
        addNotification: (notification: Record<string, any>) => unknown
        removeNotification: (uid: number | string) => unknown
      }
    | null
    | undefined

  _navigate = (path: string) => {
    if (NavigationService.isInitialized()) NavigationService.push(path)
  }

  _removeNotification(id) {
    const notificationObject = this.state.listNotifications[id]

    if (this._notifications && notificationObject && notificationObject.uid) {
      this._notifications.removeNotification(notificationObject.uid)
    }
  }

  _addNotification = (notification: UINotification) => {
    if (notification) {
      const listId = cuid()

      const retry = () => {
        if (notification.retry) {
          notification.retry()
        }

        this._removeNotification(listId)
      }

      if (this._notifications && notification._replaceNotificationWithUID) {
        this._notifications.removeNotification(
          notification._replaceNotificationWithUID,
        )
      }

      const notificationObject = this._notifications?.addNotification({
        ...notification,
        action:
          notification.action && 'link' in notification.action
            ? {
                ...notification.action,
                callback: () => {
                  if (notification.action && 'link' in notification.action) {
                    this._navigate(notification.action.link)
                  }
                },
              }
            : notification.action,
        message: notification.spinner ? (
          <Flex align="center" gap="2">
            <Spinner />
            <Text>{notification.message}</Text>
          </Flex>
        ) : (
          parseMessage({
            message: notification.message,
            retry,
          })
        ),
        position: 'bl',
        autoDismiss: notification.dismissible
          ? 0
          : notification.autoDismiss ??
            notificationDismissDelays[notification.level],
        dismissible: notification.dismissible || false,
      })
      this.setState({
        listNotifications: {
          ...this.state.listNotifications,
          [listId]: notificationObject,
        },
      })
    }
  }

  render() {
    return (
      <NotificationSystem
        ref={r => {
          this._notifications = r

          if (r) {
            setNotificationSystemRef({
              ...r,
              addNotification: this._addNotification, // override addNotification implementation with our own
            })
          } else {
            setNotificationSystemRef(null)
          }
        }}
        style={style}
      />
    )
  }
}

export default UINotificationSystem
